import { FormModal, SubmitProps } from '@/components/form-modal';
import { useToast } from '@/components/ui/hooks/use-toast';
import {
  bulkUpdateStudentGroupReassignmentRequests,
  bulkUpdateStudentGroups,
  getPendingStudentGroupReassignmentRequests,
  getStudentGroup,
  getStudentGroups,
} from '@/lib/api-calls';
import { AllUpdateTeacherTypeValues } from '@/lib/coach-utils';
import { airServerErrorFrom } from '@/lib/error-handling';
import { bulkReassignTeacherFormSchema, getDefaultSpecialRequirements } from '@/lib/student-group-reassignment-utils';
import { BulkReassignTeacherForm } from '@/routes/_layout/student-groups/-components/bulk-reassign-teacher-form';
import { LoaderIcon } from '@/components/ui/loader-icon';
import { FrequentTimeZones, maxPlainDate } from '@airreading/common';
import {
  EditStudentGroupBulkActions,
  PendingStudentGroupReassignmentRequest,
  ScheduleFromStudentGroupBulkActions,
} from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MouseEvent,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
  useMemo,
  useState,
} from 'react';
import { DefaultValues } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

type ReassignTeacherFormValues = z.infer<typeof bulkReassignTeacherFormSchema>;

export interface ReassignTeacherProps {
  reassignmentRequest: PendingStudentGroupReassignmentRequest;
  onSuccess?: () => void;
  trigger: ReactNode;
}

export const ReassignTeacher = ({ reassignmentRequest, onSuccess, trigger }: ReassignTeacherProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({ mutationFn: bulkUpdateStudentGroups });
  const [queriesEnabled, setQueriesEnabled] = useState(false);

  const lazyTrigger = isValidElement(trigger)
    ? cloneElement(trigger as ReactElement<{ onClick?: MouseEventHandler }>, {
        onClick: (e: MouseEvent) => {
          setQueriesEnabled(true);
          (trigger as ReactElement<{ onClick?: MouseEventHandler }>).props.onClick?.(e);
        },
      })
    : trigger;

  const { data: studentGroup, isError: isStudentGroupError } = useQuery({
    ...getStudentGroup(reassignmentRequest.studentGroupId),
    enabled: queriesEnabled,
  });

  const effectivePlainDate = reassignmentRequest.effectivePlainDate;
  const scheduleEndPlainDate =
    studentGroup?.overrideEndPlainDate ?? studentGroup?.classTime.schedule.endPlainDate ?? effectivePlainDate;
  const endPlainDate = maxPlainDate(scheduleEndPlainDate, effectivePlainDate);
  const timeZone = studentGroup?.classTime.schedule.school.org.timeZone ?? FrequentTimeZones.UsCentral;

  const defaultValues: DefaultValues<ReassignTeacherFormValues> = useMemo(
    () => ({
      startPlainDate: effectivePlainDate,
      endPlainDate,
      updateType: ScheduleFromStudentGroupBulkActions.FromNow,
      teacherType: AllUpdateTeacherTypeValues.Available,
      specialRequirements: getDefaultSpecialRequirements([reassignmentRequest]),
      reassignmentNotes: '',
      requiresReassignmentNotes: false,
    }),
    [effectivePlainDate, endPlainDate, reassignmentRequest],
  );

  const onSubmit = ({ formData, closeModal }: SubmitProps<ReassignTeacherFormValues>) => {
    const selectedCoach =
      formData.teacherType === AllUpdateTeacherTypeValues.Available ? formData.coachAvailable : formData.coachAll;

    if (!selectedCoach?.value) {
      toast({ title: 'Please select a teacher' });
      return;
    }

    if (!studentGroup) {
      toast({ title: 'Student group details are still loading. Please try again.' });
      return;
    }

    const isFromEffectiveDate = formData.updateType === ScheduleFromStudentGroupBulkActions.FromNow;
    const startPlainDate = isFromEffectiveDate ? effectivePlainDate : formData.startPlainDate;
    const endPlainDateForUpdate = isFromEffectiveDate
      ? maxPlainDate(scheduleEndPlainDate, startPlainDate)
      : formData.endPlainDate;

    if (Temporal.PlainDate.compare(startPlainDate, endPlainDateForUpdate) > 0) {
      toast({ title: 'Start date must be before end date' });
      return;
    }

    const specialRequirements = formData.specialRequirements?.trim() || null;
    const notesRequired = !!formData.requiresReassignmentNotes;
    const reassignmentNotes = notesRequired ? formData.reassignmentNotes?.trim() || null : null;

    if (notesRequired && !reassignmentNotes) {
      toast({ title: 'Please enter reassignment notes' });
      return;
    }

    return mutateAsync({
      bulkType: EditStudentGroupBulkActions.UpdateTeacher,
      studentGroupIds: [reassignmentRequest.studentGroupId],
      coachId: selectedCoach.value,
      startPlainDate,
      endPlainDate: endPlainDateForUpdate,
      timeZone,
    })
      .then(async ({ success, errors }) => {
        const allSucceeded = success === 1 && !errors?.length;

        if (allSucceeded) {
          await bulkUpdateStudentGroupReassignmentRequests({
            reassignmentRequestIds: [reassignmentRequest.id],
            specialRequirements,
            reassignmentNotes,
          });
        }

        if (errors?.length) {
          toast({
            title: success === 0 ? 'The student group could not be reassigned' : 'The student group was updated',
            description: (
              <ul>
                {errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            ),
            variant: 'destructive',
          });
        } else if (success === 0) {
          toast({ title: 'The student group could not be reassigned' });
        } else {
          toast({ title: 'The student group was reassigned' });
        }

        queryClient.invalidateQueries(getStudentGroups());
        queryClient.invalidateQueries(getPendingStudentGroupReassignmentRequests());
        queryClient.invalidateQueries(getStudentGroup(reassignmentRequest.studentGroupId));

        if (allSucceeded) {
          closeModal();
          onSuccess?.();
        }
      })
      .catch((error) => {
        toast({
          title: 'There was an error. Please try again.',
          description: airServerErrorFrom(error)?.userFacingMessage,
          variant: 'destructive',
        });
      });
  };

  return (
    <FormModal
      modalTitle="Reassign teacher"
      formSchema={bulkReassignTeacherFormSchema}
      defaultValues={defaultValues}
      trigger={lazyTrigger}
      onSubmit={onSubmit}
      onClose={() => setQueriesEnabled(false)}
      confirmBtnLabel="Confirm"
      dialogContentProps={{ className: 'max-w-lg lg:max-w-lg max-h-none gap-3 p-4' }}
      submitButtonProps={{ disabled: !studentGroup }}
    >
      {({ form }) =>
        studentGroup ? (
          <BulkReassignTeacherForm
            form={form}
            reassignmentRequests={[reassignmentRequest]}
            studentGroups={[studentGroup]}
            effectivePlainDate={effectivePlainDate}
            endPlainDate={endPlainDate}
          />
        ) : isStudentGroupError ? (
          <p className="text-sm text-destructive py-4">Failed to load class details. Please try again later.</p>
        ) : (
          <div className="flex justify-center py-8">
            <LoaderIcon className="h-5 w-5" />
          </div>
        )
      }
    </FormModal>
  );
};
